"use client"

import "./globals.css"
import { useEffect, useState } from "react"
import posthog from "posthog-js"
import { errorBoundaryCaptureProperties } from "@/lib/observability/posthog-scrub"
import { ErrorFallbackContent } from "@/components/error-fallback-content"
import { fontVariableClassName, lightTheme } from "@/lib/root-theme"

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Read while rendering: mounting this component's own <html> makes React
  // strip the translated-ltr class from the live element before any effect
  // runs, so the translation probe has to run first.
  const [captureProperties] = useState(() =>
    typeof document === "undefined" ? undefined : errorBoundaryCaptureProperties(error.digest, document),
  )
  useEffect(() => {
    // PostHog error tracking. A no-op where posthog-js never initialized (the
    // private messaging routes, an opted-out internal device); outside
    // production the before_send in instrumentation-client.ts drops it.
    posthog.captureException(error, captureProperties)
  }, [error, captureProperties])

  return (
    <html lang="en" className={fontVariableClassName} style={lightTheme}>
      <body className="min-h-screen bg-[var(--color-paper)] text-[var(--color-ink)] antialiased">
        <ErrorFallbackContent
          reset={reset}
          eyebrow="Something broke"
          description="We tried to capture the error automatically. Try the action again, or come back in a moment."
          showHomeLink={false}
          digest={error.digest}
        />
      </body>
    </html>
  )
}
